import FeaturedLastNews from '@/components/Home/FeaturedLastNews';
import Layout from '@/components/Layout';
import Breadcrumb from '@/components/Layout/Breadcrumb';
import WrapperContent from '@/components/Layout/WrapperContent';
import { NavBarContent } from '@/components/Navbar';
import ProfilePage from '@/components/Profile';
import { ProfileAdContainer } from '@/components/Profile/styles';
import useBreakpoint from '@/hooks/useBreakpoint';
import { FeaturedNewsApi } from '@/pages/[categorias]/[...news]';
import { api, mimir, site } from '@/services/api';
import { NavBarContentData, TimeLineApi } from '@/shared/types/home.types';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import { AuthorProfileResponseData, mergeAuthorProfile } from '@/utils/mergeAuthorProfile';
import * as Sentry from '@sentry/nextjs';
import axios from 'axios';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

const AdsGeneratorMemo = dynamic(() => import('@/components/Ads/Google/AdsTypeGeneratorsMemo'), { ssr: false });

export interface AuthorProfileProps {
  responseData: AuthorProfileResponseData;
  navBarData: NavBarContent;
  timelineData: Pick<FeaturedNewsApi, 'data'>;
}

export default function AuthorProfile({ responseData, navBarData, timelineData = null }: AuthorProfileProps) {
  const router = useRouter();
  const { autor } = router.query;
  const { data, segmentacoes, schema, ssr, breadcrumb } = responseData;
  const { nome, imagem, links, texto } = data;

  const [isMobile] = useBreakpoint('tablet');

  return (
    <Layout layoutData={{ navBarData, segmentacoes, tagsData: ssr, schema, breadcrumbList: breadcrumb }}>
      <WrapperContent isOnHeader hasBreadcrumb>
        <Breadcrumb breadcrumbList={breadcrumb} />
        <ProfilePage nome={nome} imagem={imagem} links={links} texto={texto} type="autor" />
      </WrapperContent>
      <ProfileAdContainer>
        <AdsGeneratorMemo type={`${isMobile ? 'rectangle' : 'billboard'}/main/listing`} marginBottomP="24px" />
      </ProfileAdContainer>
      {timelineData && timelineData?.data?.timeline && (
        <FeaturedLastNews
          featuredLastNewsData={timelineData.data.timeline}
          rootPage={`equipe/${autor}`}
          textTitle={`Por ${nome}`}
          route={autor as string}
          routeType="autor"
        />
      )}
    </Layout>
  );
}

// Teto de espera pelo monolito no merge: um monolito lento não pode atrasar a
// página quando o Directus já resolveu o autor. Estourado o teto, degrada pra
// Directus-only — o monolito só preenche campos ausentes, é dispensável aqui.
const MONO_MERGE_TIMEOUT_MS = 2500;

/** Resolve com o valor da promise, ou `null` se ela exceder `ms` ou rejeitar. */
function settleWithinOrNull<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<null>(resolve => {
    timer = setTimeout(() => resolve(null), ms);
  });
  return Promise.race([promise.catch(() => null), timeout]).finally(() => clearTimeout(timer));
}

// Formato de todos os `nome_url` ativos: minúsculas, dígitos e hífens. Validar na
// fronteira evita interpolar entrada hostil (ex.: `../`, `<script>`) nos paths de
// upstream (mimir/site) e normaliza variação de caixa pro slug canônico.
const AUTHOR_SLUG_RE = /^[a-z0-9-]+$/;

function normalizeAutorSlug(rawAutor: string | string[] | undefined): string | null {
  const value = Array.isArray(rawAutor) ? rawAutor[0] : rawAutor;
  if (!value) return null;
  const slug = value.trim().toLowerCase();
  return AUTHOR_SLUG_RE.test(slug) ? slug : null;
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { host } = context.req.headers;
  const autorSlug = normalizeAutorSlug(context.query.autor);
  if (!autorSlug) return { notFound: true };

  const profilePath = `/equipe/${autorSlug}/`;
  const menuPath = `/componentes/menu/`;
  const timelinePath = `/timelines/ultimas/autor/${autorSlug}`;

  // Directus (via Mimir) é a fonte base do perfil; o monolito preenche por
  // campo os dados exibidos (foto, perfil, redes) quando ausentes no Directus.
  // Perfil e auxiliares (menu, timeline) disparam em paralelo. O monolito NÃO
  // entra no `allSettled` aguardado: quando o Directus é autoritativo, esperá-lo
  // acoplaria a latência da página à saúde do monolito. Ele é aguardado com teto
  // curto no merge (abaixo) e com orçamento cheio só no fallback legado.
  const directusPromise = mimir.get<AuthorProfileResponseData>(profilePath);
  const monoPromise = Promise.resolve(site.get<AuthorProfileResponseData>(profilePath));
  // Consumidor condicional — evita unhandled rejection se o monolito falhar e a
  // página seguir só com o Directus.
  monoPromise.catch(() => null);

  const [directusResult, menuResult, timelineResult] = await Promise.allSettled([
    directusPromise,
    api.get<NavBarContentData>(menuPath),
    mimir.get<TimeLineApi>(timelinePath),
  ]);

  const menuResponse = menuResult.status === 'fulfilled' ? menuResult.value : null;
  const timelineResponse = timelineResult.status === 'fulfilled' ? timelineResult.value : null;

  // Perfil do Directus só é autoritativo com status 0 do envelope CanalTech.
  // O Mimir sempre devolve status 0 em HTTP 200 (404 quando não acha), mas a
  // guarda mantém o caminho simétrico ao do monolito e resiliente a mudança de
  // contrato: qualquer outro status cai no fallback legado (redirect/notFound).
  const directusProfile = directusResult.status === 'fulfilled' ? directusResult.value?.data ?? null : null;

  if (directusProfile?.status === 0) {
    // O monolito só entra como fonte de preenchimento se responder um perfil
    // válido (status 0) dentro do teto — um monolito lento degrada pra
    // Directus-only em vez de travar a página. Redirect/404/erro são ignorados:
    // o Directus é autoritativo quando tem o autor.
    const monoResponse = await settleWithinOrNull(monoPromise, MONO_MERGE_TIMEOUT_MS);
    const monoProfile = monoResponse?.data?.status === 0 ? monoResponse.data : null;

    const responseData = monoProfile ? mergeAuthorProfile(directusProfile, monoProfile) : directusProfile;

    const { headers } = responseData;
    if (headers) {
      context.res.setHeader('Cache-Control', headers['Cache-control']);
      context.res.setHeader('Expires', headers.Expires);
    }
    context.res.statusCode = 200;

    return {
      props: {
        responseData,
        navBarData: menuResponse?.data?.data ?? null,
        timelineData: timelineResponse?.data ?? null,
      },
    };
  }

  // Falha do Mimir: 404 é o caminho esperado (autor só existe no monolito).
  // Outros erros (5xx, timeout, parse) viram observabilidade pra distinguir
  // tráfego saudável de degradação em produção. Só há erro a capturar quando o
  // Mimir rejeitou — um 200 com status ≠ 0 apenas cai no fallback, sem exceção.
  if (directusResult.status === 'rejected') {
    const profileError = directusResult.reason;
    const isExpectedMimirNotFound = axios.isAxiosError(profileError) && profileError.response?.status === 404;
    if (!isExpectedMimirNotFound) {
      Sentry.captureException(profileError, {
        tags: { source: 'mimir-equipe-profile' },
        extra: { slug: autorSlug },
      });
    }
  }

  // Autor ausente/degradado no Directus: o monolito vira a fonte única, agora
  // com orçamento cheio (o teto curto acima vale só pro merge). Reaproveita a
  // mesma promise já disparada e as auxiliares já obtidas — sem chamada extra.
  const profileReq = { promise: monoPromise, link: profilePath };
  const responseMenuReq = {
    promise: menuResponse ? Promise.resolve(menuResponse) : api.get<NavBarContentData>(menuPath),
    link: menuPath,
  };
  const timelinesReq = {
    promise: timelineResponse ? Promise.resolve(timelineResponse) : mimir.get<TimeLineApi>(timelinePath),
    link: timelinePath,
  };

  // O monolito é a fonte única aqui. Se estiver indisponível/errado, o
  // `getConcurrentPromises` pode propagar a exceção do `validatesResponse`
  // (upstream sem status conhecido). Degrada pra notFound em vez de 500 e
  // registra a falha de upstream pra observabilidade.
  let concurrent: Awaited<ReturnType<typeof getConcurrentPromises>>;
  try {
    concurrent = await getConcurrentPromises({
      mainPromise: profileReq,
      navBarPromise: responseMenuReq,
      timelinePromise: timelinesReq,
      host,
    });
  } catch (fallbackError) {
    Sentry.captureException(fallbackError, {
      tags: { source: 'equipe-fallback' },
      extra: { slug: autorSlug },
    });
    return { notFound: true };
  }

  const { promiseData, data, statusCode } = concurrent;

  if (data.redirect) return data;

  if (data.notFound) return data;

  const navBarData: NavBarContentData | null = promiseData[responseMenuReq.link]?.data?.data ?? null;
  const timelineData: TimeLineApi | null = promiseData[timelinesReq.link]?.data ?? null;

  const { headers } = data;
  const responseData = data;

  // Guarda defensiva: um `data` de fallback sem `headers` (upstream degradado)
  // não pode derrubar a resposta na hora de setar o cache.
  if (headers) {
    context.res.setHeader('Cache-Control', headers['Cache-control']);
    context.res.setHeader('Expires', headers.Expires);
  }
  context.res.statusCode = statusCode;

  return {
    props: {
      responseData,
      navBarData,
      timelineData,
    },
  };
};
